import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import type { ActivationChecklistItemId } from './types';

type ActivationChecklistMessages = {
	dismissChecklist: MessageDescriptor;
	itemCompleted: MessageDescriptor;
	createWorkItemTitle: MessageDescriptor;
	createWorkItemDescription: MessageDescriptor;
	createWorkItemCta: MessageDescriptor;
	updateWorkItemTitle: MessageDescriptor;
	updateWorkItemDescription: MessageDescriptor;
	updateWorkItemCta: MessageDescriptor;
	addCommentTitle: MessageDescriptor;
	addCommentDescription: MessageDescriptor;
	addCommentCta: MessageDescriptor;
	inviteTeamTitle: MessageDescriptor;
	inviteTeamDescription: MessageDescriptor;
	inviteTeamCta: MessageDescriptor;
};

type ActivationChecklistCopy = {
	title: string;
	description: string;
	ctaLabel: string;
};

export const activationChecklistMessages: ActivationChecklistMessages = defineMessages({
	dismissChecklist: {
		id: 'growth-starburst.wac-product-activation-checklist.dismiss-checklist',
		defaultMessage: 'Dismiss checklist',
		description: 'Button label for dismissing the whole Jira activation checklist',
	},
	itemCompleted: {
		id: 'growth-starburst.wac-product-activation-checklist.item-completed',
		defaultMessage: 'Completed',
		description:
			'Accessible status announced to screen readers for a completed Jira activation checklist item',
	},
	createWorkItemTitle: {
		id: 'growth-starburst.wac-product-activation-checklist.create-work-item.title',
		defaultMessage: 'Create your first Jira work item',
		description: 'Title for the Jira activation checklist row that creates a first work item',
	},
	createWorkItemDescription: {
		id: 'growth-starburst.wac-product-activation-checklist.create-work-item.description',
		defaultMessage: 'Use AI to draft a work item in your most recent Jira Software project.',
		description: 'Description for the Jira activation checklist row that creates a first work item',
	},
	createWorkItemCta: {
		id: 'growth-starburst.wac-product-activation-checklist.create-work-item.cta',
		defaultMessage: 'Create work item',
		description: 'CTA label for creating a Jira work item from the activation checklist',
	},
	updateWorkItemTitle: {
		id: 'growth-starburst.wac-product-activation-checklist.update-work-item.title',
		defaultMessage: 'Update a work item',
		description: 'Title for the Jira activation checklist row that updates a work item',
	},
	updateWorkItemDescription: {
		id: 'growth-starburst.wac-product-activation-checklist.update-work-item.description',
		defaultMessage: 'Open a recent work item and update one of its fields.',
		description: 'Description for the Jira activation checklist row that updates a work item',
	},
	updateWorkItemCta: {
		id: 'growth-starburst.wac-product-activation-checklist.update-work-item.cta',
		defaultMessage: 'Update work item',
		description: 'CTA label for updating a Jira work item from the activation checklist',
	},
	addCommentTitle: {
		id: 'growth-starburst.wac-product-activation-checklist.add-comment.title',
		defaultMessage: 'Add a comment to a work item',
		description: 'Title for the Jira activation checklist row that adds a comment',
	},
	addCommentDescription: {
		id: 'growth-starburst.wac-product-activation-checklist.add-comment.description',
		defaultMessage: 'Jump back into a recent work item and leave a comment for your team.',
		description: 'Description for the Jira activation checklist row that adds a comment',
	},
	addCommentCta: {
		id: 'growth-starburst.wac-product-activation-checklist.add-comment.cta',
		defaultMessage: 'Add comment',
		description: 'CTA label for adding a comment from the Jira activation checklist',
	},
	inviteTeamTitle: {
		id: 'growth-starburst.wac-product-activation-checklist.invite-team.title',
		defaultMessage: 'Invite your team',
		description: 'Title for the Jira activation checklist row that invites teammates',
	},
	inviteTeamDescription: {
		id: 'growth-starburst.wac-product-activation-checklist.invite-team.description',
		defaultMessage: 'Bring teammates into Jira so everyone can collaborate in one place.',
		description: 'Description for the Jira activation checklist row that invites teammates',
	},
	inviteTeamCta: {
		id: 'growth-starburst.wac-product-activation-checklist.invite-team.cta',
		defaultMessage: 'Invite team',
		description: 'CTA label for inviting teammates from the Jira activation checklist',
	},
});

const checklistCopyByItemId: Record<
	ActivationChecklistItemId,
	{
		title: MessageDescriptor;
		description: MessageDescriptor;
		ctaLabel: MessageDescriptor;
	}
> = {
	'jira-create-work-item': {
		title: activationChecklistMessages.createWorkItemTitle,
		description: activationChecklistMessages.createWorkItemDescription,
		ctaLabel: activationChecklistMessages.createWorkItemCta,
	},
	'jira-update-work-item': {
		title: activationChecklistMessages.updateWorkItemTitle,
		description: activationChecklistMessages.updateWorkItemDescription,
		ctaLabel: activationChecklistMessages.updateWorkItemCta,
	},
	'jira-add-comment': {
		title: activationChecklistMessages.addCommentTitle,
		description: activationChecklistMessages.addCommentDescription,
		ctaLabel: activationChecklistMessages.addCommentCta,
	},
	'jira-invite-team': {
		title: activationChecklistMessages.inviteTeamTitle,
		description: activationChecklistMessages.inviteTeamDescription,
		ctaLabel: activationChecklistMessages.inviteTeamCta,
	},
};

export const getActivationChecklistCopy = (
	intl: Pick<IntlShape, 'formatMessage'>,
	id: ActivationChecklistItemId,
): ActivationChecklistCopy => {
	const copy = checklistCopyByItemId[id];

	return {
		title: intl.formatMessage(copy.title),
		description: intl.formatMessage(copy.description),
		ctaLabel: intl.formatMessage(copy.ctaLabel),
	};
};
